import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { expiredSessionLogin } from '../lib/authDestination';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const validUser = (value) => value && typeof value === 'object' && typeof value.id === 'string' && typeof value.role === 'string';

// Ensure all axios requests include credentials (cookies)
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

const isExpiredOrInvalidTokenError = (error) => {
  const detail = error?.response?.data?.detail || '';
  return detail.includes('Token expiré') || detail.includes('Token invalide');
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState('loading');
  const interceptorRef = useRef(null);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setAuthStatus('unauthenticated');
  }, []);

  const applyAuthData = useCallback((accessToken, userData) => {
    if (accessToken) {
      localStorage.setItem('token', accessToken);
      setToken(accessToken);
    }
    setUser(userData);
    setAuthStatus('authenticated');
  }, []);

  // Axios interceptor: auto-logout only when the token is explicitly expired/invalid.
  useEffect(() => {
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          if (isExpiredOrInvalidTokenError(error)) {
            clearAuth();
            window.location.href = expiredSessionLogin(window.location.pathname, window.location.search);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [token, clearAuth]);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      setLoading(true);
      setAuthStatus('loading');

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
        if (cancelled) return;
        if (!validUser(response.data)) throw new Error('Réponse de session invalide');
        setUser(response.data);
        setAuthStatus('authenticated');
      } catch (error) {
        if (cancelled) return;

        if (token && isExpiredOrInvalidTokenError(error)) {
          clearAuth();
        } else if (token && (!error.response || error.response.status >= 500)) {
          console.warn('Auth check unavailable:', error.response?.status || 'network');
          setAuthStatus('uncertain');
        } else {
          setUser(null);
          setAuthStatus('unauthenticated');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initAuth();
    return () => {
      cancelled = true;
    };
  }, [token, clearAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    if (!access_token || !validUser(userData)) throw new Error('Réponse de connexion invalide');
    applyAuthData(access_token, userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    if (!access_token || !validUser(userData)) throw new Error('Réponse de connexion invalide');
    applyAuthData(access_token, userData);
    return userData;
  };

  const refreshUser = async () => {
    const response = await axios.get(`${API_URL}/api/auth/me`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!validUser(response.data)) throw new Error('Réponse de session invalide');
    setUser(response.data);
    return response.data;
  };

  const setAuthData = useCallback((accessToken, userData) => {
    applyAuthData(accessToken, userData);
  }, [applyAuthData]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (e) {
      // Ignore errors clearing the cookie server-side
    }
    clearAuth();
  }, [clearAuth]);

  const isSessionActive = !!user || authStatus === 'uncertain';
  const hasEditorRole = user?.role === 'editor' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  // Subscription helpers
  const subscription = user?.subscription || { status: 'none', plan_id: 'free', features: [] };
  const hasActiveSubscription = subscription.status === 'active';
  const canReadPremium = hasEditorRole || (hasActiveSubscription && subscription.plan_id !== 'free');
  const canUseEditor = hasEditorRole || (hasActiveSubscription && ['redacteur', 'licence'].includes(subscription.plan_type));
  const isSubscriber = hasActiveSubscription && subscription.plan_id !== 'free';

  // isEditor = rôle EDITOR/ADMIN OU abonné Rédacteur actif. Backward compatible :
  // tous les rôles existants restent traités comme avant (hasEditorRole les inclut).
  const isEditor = canUseEditor;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      authStatus,
      login,
      register,
      refreshUser,
      setAuthData,
      logout,
      isEditor,
      isAdmin,
      isAuthenticated: isSessionActive,
      subscription,
      hasActiveSubscription,
      canReadPremium,
      canUseEditor,
      isSubscriber,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
